// src/components/Admin/QuestionCurated.js
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import QuestionsContainer from './QuestionsContainer';
import PopupWrapper from '../../components/UI/PopupWrapper';
import AnswerQuestionModal from '../../components/Modals/AnswerQuestionModal';
import ViewAnswerModal from '../../components/Modals/ViewAnswerModal';
import ReceivedPopup from '../../components/UI/ReceivedPopup';
import CurateQuestionModal from '../../components/Modals/CurateQuestionModal';

const QuestionCurated = () => {
  const { currentUser } = useContext(AuthContext);
  const [questions, setQuestions] = useState([]);
  const [activePopupId, setActivePopupId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Load only curated questions for the current user on mount.
  useEffect(() => {
    if (currentUser && currentUser.username) {
      fetchQuestions();
    }
  }, [currentUser]);

  const fetchQuestions = () => {
    fetch('/api/questions')
      .then(res => res.json())
      .then(data => {
        // Filter: only include questions that are curated and belong to the current user.
        const curated = (data || [])
          .filter(q => q.isCurated && q.toUsername === currentUser.username)
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setQuestions(curated);
      })
      .catch(err => console.error(err));
  };

  const handleDeleteQuestion = async (id) => {
    setQuestions(prev => prev.filter(q => q._id !== id));
    try {
      const response = await fetch(`/api/questions/${id}`, { method: 'DELETE' });
      if (!response.ok) {
        console.error("Failed to delete question:", id);
        fetchQuestions();
      }
    } catch (err) {
      console.error(err);
      fetchQuestions();
    }
    setConfirmDeleteId(null);
  };

  const handleHideQuestion = async (id) => {
    setQuestions(prev =>
      prev.map(q => q._id === id ? { ...q, displayOnProfile: false } : q)
    );
    try {
      const response = await fetch(`/api/questions/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayOnProfile: false }),
      });
      if (!response.ok) {
        console.error("Failed to hide question:", id);
        fetchQuestions();
      }
    } catch (err) {
      console.error(err);
      fetchQuestions();
    }
  };

  const handleShowQuestion = async (id) => {
    setQuestions(prev =>
      prev.map(q => q._id === id ? { ...q, displayOnProfile: true } : q)
    );
    try {
      const response = await fetch(`/api/questions/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayOnProfile: true }),
      });
      if (!response.ok) {
        console.error("Failed to show question:", id);
        fetchQuestions();
      }
    } catch (err) {
      console.error(err);
      fetchQuestions();
    }
  };

  const handleReplyClick = (questionText, detail, id) => {
    setSelectedQuestion({ _id: id.toString(), question: questionText, detail });
    setConfirmDeleteId(null);
    setActivePopupId(null);
  };

  const handleViewAnswer = (questionText, detail, id, answer, tags) => {
    setSelectedQuestion({ _id: id.toString(), question: questionText, detail, answer, tags });
    setConfirmDeleteId(null);
    setActivePopupId(null);
  };

  const handlePopupToggle = (id) => {
    setActivePopupId(prev => (prev === id ? null : id));
    setConfirmDeleteId(null);
  };

  const handleOpenConfirm = (id) => {
    setConfirmDeleteId(id);
  };

  const handlePopupClose = () => {
    setActivePopupId(null);
    setConfirmDeleteId(null);
  };

  const handleAnswerPosted = (updatedQuestion) => {
    setQuestions(prev =>
      prev.map(q =>
        q._id.toString() === updatedQuestion._id.toString() ? updatedQuestion : q
      )
    );
    setSelectedQuestion(null);
  };

  const handleToggleDisplay = (q) => {
    if (q.displayOnProfile) {
      handleHideQuestion(q._id);
    } else {
      handleShowQuestion(q._id);
    }
  };

  const handleCreateQuestionClick = () => {
    setShowCreateModal(true);
  };

  const handleQuestionCreated = (newQuestion) => {
    // Prepend the new curated question to the list.
    setQuestions(prev => [newQuestion, ...prev]);
    setShowCreateModal(false);
  };

  // Filter questions based on search term.
  const filteredQuestions = questions.filter(q => {
    const search = searchTerm.toLowerCase();
    const questionText = q.question.toLowerCase();
    const dateString = new Date(q.createdAt).toLocaleDateString().toLowerCase();
    // For curated questions, sender is not usually visible but we include it just in case.
    let sender = '';
    if(q.fromUsername) sender += q.fromUsername.toLowerCase();
    if(q.askedName) sender += q.askedName.toLowerCase();
    if(q.toUsername) sender += q.toUsername.toLowerCase();
    return questionText.includes(search) || dateString.includes(search) || sender.includes(search);
  });

  const tableHead = (
    <tr>
      <th>Question</th>
      <th>Date Added</th>
      <th>Display on Profile</th>
      <th>Actions</th>
    </tr>
  );

  const renderDesktopRow = (q, index) => {
    const qId = q._id ? q._id.toString() : "";
    const displayText = q.displayOnProfile ? "Yes" : "No";
    return (
      <tr key={qId} className={index === 0 ? "highlight" : ""}>
        <td>{q.question}</td>
        <td>{new Date(q.createdAt).toLocaleDateString()}</td>
        <td>{displayText}</td>
        <td className="table-action">
          {/* Curated questions are never pending, so always show "View answer" */}
          <button
            className="table-btn action-button"
            onClick={() => handleViewAnswer(q.question, q.detail, q._id, q.answer, q.tags)}
          >
            <img src="/images/i_table_view.svg" alt="View answer" />
          </button>
          <PopupWrapper onClose={handlePopupClose}>
            <>
              <button
                className="table-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePopupToggle(q._id);
                }}
              >
                <img className="more" src="/images/i_More.svg" alt="More" />
              </button>
              {activePopupId === q._id && (
                <ReceivedPopup
                  q={q}
                  onReply={() => handleViewAnswer(q.question, q.detail, q._id, q.answer, q.tags)}
                  onDelete={() => handleDeleteQuestion(q._id)}
                  onToggleDisplay={() => handleToggleDisplay(q)}
                  onClose={handlePopupClose}
                  confirm={confirmDeleteId === q._id}
                  onOpenConfirm={() => handleOpenConfirm(q._id)}
                />
              )}
            </>
          </PopupWrapper>
        </td>
      </tr>
    );
  };

  const renderMobileCard = (q) => {
    const qId = q._id ? q._id.toString() : "";
    // Since curated questions have no pending state, always use "View answer"
    const mainAction = () => handleViewAnswer(q.question, q.detail, q._id, q.answer, q.tags);
    const mainActionLabel = "View answer";

    return (
      <div className="card" key={qId} style={{ position: "relative" }}>
        <p className="xlarge">{q.question}</p>
        <button className="grey full-width margin-top-m" onClick={mainAction}>
          <img src="/images/i_View_Answer.svg" alt={mainActionLabel} />
        </button>
        <PopupWrapper onClose={handlePopupClose}>
          <>
            <button className="clear full-width" onClick={(e) => { e.stopPropagation(); handlePopupToggle(q._id); }}>
              <img src="/images/i_More.svg" alt="More" />
            </button>
            {activePopupId === q._id && (
              <ReceivedPopup
                q={q}
                onReply={() => handleViewAnswer(q.question, q.detail, q._id, q.answer, q.tags)}
                onDelete={() => handleDeleteQuestion(q._id)}
                onToggleDisplay={() => handleToggleDisplay(q)}
                onClose={handlePopupClose}
                confirm={confirmDeleteId === q._id}
                onOpenConfirm={() => handleOpenConfirm(q._id)}
              />
            )}
          </>
        </PopupWrapper>
      </div>
    );
  };

  return (
    <>
      {/* Header with a button to create a new curated question */}
      <div className="admin-action-header">
        <div className="search-container">
          <input 
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="btn" onClick={handleCreateQuestionClick}>
          Curate a New Q&A
        </button>
      </div>
      <QuestionsContainer
        questions={filteredQuestions}
        renderDesktopRow={renderDesktopRow}
        renderMobileCard={renderMobileCard}
        tableHead={tableHead}
        hidePendingToggle={true}
      />
      {selectedQuestion && (
        selectedQuestion.answer ? (
          <ViewAnswerModal
            onClose={() => setSelectedQuestion(null)}
            question={selectedQuestion.question}
            detail={selectedQuestion.detail}
            answer={selectedQuestion.answer}
            questionId={selectedQuestion._id}
            initialTags={selectedQuestion.tags || []}
            onAnswerPosted={handleAnswerPosted}
          />
        ) : (
          <AnswerQuestionModal
            onClose={() => setSelectedQuestion(null)}
            questionId={selectedQuestion._id}
            question={selectedQuestion.question}
            detail={selectedQuestion.detail}
            onAnswerPosted={handleAnswerPosted}
          />
        )
      )}
      {showCreateModal && (
        <CurateQuestionModal
          onClose={() => setShowCreateModal(false)}
          onQuestionCreated={handleQuestionCreated}
          currentUser={currentUser}
        />
      )}
    </>
  );
};

export default QuestionCurated;
